import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Plus, RefreshCw } from 'lucide-react';
import legacyUapiService from '../services/LegacyUapiService';
import { useAccount } from '../contexts/AccountContext';
import { formatDateTime } from '../utils/datetime';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import './OhipTransactionsPage.css';
import './UniversalApiPage.css';
import './PaymentPorterProcessorPage.css';   // .ppp-detail-btn — the site's list-row Details button

// Legacy > Universal API > Authentication — a PROPERTY's credentials for calling the Universal API.
//
// The provisioning half of the split that put the API's definition under Admin > Platform. Two states,
// decided by the hotel picker in the top bar:
//
//   no hotel   an estate list of the hotels that HAVE a credential, narrowed to whatever context is
//              selected (a chain lists its hotels; a partner, everything beneath it). Answers "who is
//              provisioned", which no screen answered before.
//   a hotel    that hotel's single credential.
//
// ONE CREDENTIAL PER HOTEL. Not a database constraint — entity_credentials is shared by every
// marketplace and several legitimately hold more than one — so the rule is enforced here and the list
// reports any hotel that breaks it rather than silently showing the first row.
//
// RENDERED TWO WAYS. On its own it is a page; inside the Universal API screen's Authentication tab it
// is the same component with `embedded`, which drops its <h1> and its page wrapper — a tab that repeats
// the page title reads as a second page bolted on, and two nested `.otx-page` blocks stack their
// padding. The actions move up beside the tab bar in that mode, where the parent puts them.
function UapiAuthenticationPage({ embedded = false, onRegisterActions }) {
    const navigate = useNavigate();
    const { selectedHotel, selectedAccount, selectedPartner } = useAccount();

    const [rows, setRows] = useState([]);
    const [duplicates, setDuplicates] = useState([]);
    const [marketplaceId, setMarketplaceId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);


    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await legacyUapiService.listCredentials({
                hotelId: selectedHotel, accountId: selectedAccount, partnerId: selectedPartner,
            });
            const d = res.data || res;
            setRows(d.credentials || []);
            setDuplicates(d.duplicates || []);
            setMarketplaceId(d.marketplaceId || null);
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Failed to load credentials');
        } finally {
            setLoading(false);
        }
    }, [selectedHotel, selectedAccount, selectedPartner]);

    useEffect(() => { load(); }, [load]);

    const scopeLabel = selectedHotel ? 'this hotel'
        : selectedAccount ? 'this chain'
        : selectedPartner ? 'this partner'
        : 'every partner';

    const actions = (
        <>
            <Button variant="secondary" onClick={() => navigate('/universal-api/authentication/new')}>
                <Plus className="icon" /> Add access
            </Button>
            <Button variant="success" onClick={load} disabled={loading}>
                <RefreshCw className="icon" /> Refresh
            </Button>
        </>
    );

    // The same two buttons either way; the embedded parent renders them itself so they line up with the
    // tab bar instead of sitting inside the panel.
    //
    // DEPS ARE REQUIRED HERE, not optional tidiness. `actions` is a new JSX object on every render, so a
    // dep-less effect would setState on the parent every render, re-render this child, and loop forever.
    // Firing on `loading` is what the parent actually needs — it is the only thing in those buttons that
    // changes — and `onRegisterActions` is a setState function, which React keeps stable.
    useEffect(() => {
        if (embedded) onRegisterActions?.(actions);
    }, [embedded, loading, onRegisterActions]);   // eslint-disable-line

    const Wrapper = embedded ? React.Fragment : 'div';
    const wrapperProps = embedded ? {} : { className: 'otx-page' };

    return (
        <Wrapper {...wrapperProps}>
            {!embedded && (
                <div className="otx-header">
                    <div>
                        <h1>Universal API Authentication and Access</h1>
                        <p className="otx-subtitle">
                            Credentials to access the Universal API and backend PMS systems
                        </p>
                    </div>
                    <div className="otx-header-actions">{actions}</div>
                </div>
            )}

            {error && (
                <div className="error-alert">
                    <div className="error-alert-content">
                        <AlertCircle className="error-alert-icon" />
                        <p className="error-alert-message">{error}</p>
                    </div>
                </div>
            )}

            {duplicates.length > 0 && (
                <div className="error-alert">
                    <div className="error-alert-content">
                        <AlertCircle className="error-alert-icon" />
                        <p className="error-alert-message">
                            {duplicates.length} hotel{duplicates.length === 1 ? '' : 's'} hold more than one
                            Universal API credential ({duplicates.join(', ')}). One per hotel is the rule —
                            the extra credentials still authenticate, so remove them in the credential screens.
                        </p>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="loading-section"><Spinner size="md" /><p>Loading credentials...</p></div>
            ) : rows.length === 0 ? (
                <div className="otx-table-wrap" style={{ padding: '3rem 2rem', textAlign: 'center' }}>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
                        No hotel under {scopeLabel} has a Universal API credential yet.
                    </p>
                    <p style={{ margin: '0.5rem auto 0', color: '#9ca3af', fontSize: '0.825rem', maxWidth: '56ch' }}>
                        Use Add access to provision one: it issues the hotel's Universal API credential
                        and records which backend system the API calls for it.
                    </p>
                </div>
            ) : (
                <div className="otx-table-wrap">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Hotel</th>
                                <th>Chain</th>
                                <th>Credential</th>
                                <th>Backend systems</th>
                                <th>Last changed</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                /* The row opens the hotel's own page: which backend systems it reaches
                                   and, for each, the x-hotelid a caller sends to reach that one. The
                                   list can only say WHICH systems; the identifiers need a page. */
                                <tr
                                    key={r.hotel_id}
                                    className="uapi-row-link"
                                    onClick={() => navigate(`/universal-api/authentication/${r.hotel_id}`)}
                                >
                                    <td>{r.hotelname}</td>
                                    <td>{r.account_name || <span className="lu-muted">—</span>}</td>
                                    <td>
                                        {r.credential_name}
                                        {Number(r.credential_count) > 1 && (
                                            <span className="uapi-dup-flag">{r.credential_count} credentials</span>
                                        )}
                                    </td>
                                    <td>
                                        {/* Pipe-separated by GROUP_CONCAT — a separator that cannot
                                            occur in a system name, unlike the comma several contain. */}
                                        {String(r.access_systems || '').split('|').filter(Boolean).length
                                            ? String(r.access_systems).split('|').filter(Boolean).map((name) => (
                                                <span key={name} className="uapi-system-chip">{name}</span>
                                              ))
                                            : <span className="lu-muted">Not routed</span>}
                                    </td>
                                    <td>{formatDateTime(r.updated_at)}</td>
                                    <td
                                        style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <button
                                            className="ppp-detail-btn ppp-detail-btn-primary"
                                            onClick={() => navigate(`/universal-api/authentication/${r.hotel_id}`)}
                                        >
                                            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                                                <path d="M2 4h12M2 8h8M2 12h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                            </svg>
                                            Details
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

        </Wrapper>
    );
}

export default UapiAuthenticationPage;
